import { useCallback, useEffect, useRef } from 'react';

/** 로그인 유지 미선택 시: 사용자 조작(클릭·키입력 등) 없으면 자동 로그아웃 */
export const IDLE_LOGOUT_MS = 60 * 60 * 1000;

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'pointerdown',
  'keydown',
  'mousemove',
  'scroll',
  'touchstart',
  'wheel',
];

/**
 * 실제 사용자 활동만 유휴 타이머를 리셋합니다.
 * (결재 뱃지 폴링 등 백그라운드 API는 리셋하지 않음)
 */
export function useIdleDeadline(
  enabled: boolean,
  idleMs: number,
  onExpire: () => void
): { getDeadline: () => number | null } {
  const deadlineRef = useRef<number | null>(null);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const getDeadline = useCallback(() => deadlineRef.current, []);

  useEffect(() => {
    if (!enabled) {
      deadlineRef.current = null;
      return;
    }

    const bump = () => {
      deadlineRef.current = Date.now() + idleMs;
    };
    bump();

    let throttleUntil = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now < throttleUntil) return;
      throttleUntil = now + 1000;
      bump();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onActivity();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const id = window.setInterval(() => {
      const deadline = deadlineRef.current;
      if (deadline != null && Date.now() >= deadline) {
        deadlineRef.current = null;
        onExpireRef.current();
      }
    }, 1000);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(id);
    };
  }, [enabled, idleMs]);

  return { getDeadline };
}
